import {
  getIdeaStatusTone,
  mapIdeaToUiState,
  type IdeaUiOverride,
} from "@/lib/ideaUi";
import { getPageWorkflowStatus } from "@/lib/pageWorkflow";
import type { Idea, Page } from "@/schemas";
import type { HeuristicJob } from "@/schemas/firestore/jobs.schema";
import type { WordPressVersion } from "@/schemas/firestore/wordpressVersion.schema";

export type UnifiedRowKind = "audit" | "page" | "idea";

export const kindLabel: Record<UnifiedRowKind, string> = {
  audit: "Audit",
  page: "Page",
  idea: "Idea",
};

export const kindClass: Record<UnifiedRowKind, string> = {
  audit: "bg-purple-50 text-purple-700 border-purple-200",
  page: "bg-blue-50 text-blue-700 border-blue-200",
  idea: "bg-amber-50 text-amber-700 border-amber-200",
};

export interface UnifiedRow {
  kind: UnifiedRowKind;
  id: string;
  title: string;
  path: string;
  status: string;
  statusTone: string;
  score: number | null;
  date: unknown;
  raw: HeuristicJob | Page | Idea;
}

// Moved from auditColumns.tsx (deleted in Task 6) — unchanged logic.
const getPriorityClass = (priority: string) => {
  switch (priority?.toLowerCase()) {
    case "high":
      return "bg-red-50 text-red-700 border-red-200";
    case "medium":
      return "bg-amber-50 text-amber-700 border-amber-200";
    case "low":
      return "bg-green-50 text-green-700 border-green-200";
    default:
      return "";
  }
};

// Moved from pageColumns.tsx (deleted in Task 6) — unchanged logic.
const getWorkflowClass = (status: string) => {
  switch (status) {
    case "Awaiting Approval":
      return "bg-amber-50 text-amber-700 border-amber-200";
    case "Draft In Progress":
      return "bg-blue-50 text-blue-700 border-blue-200";
    case "Approved":
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    case "Publishing":
      return "bg-indigo-50 text-indigo-700 border-indigo-200";
    case "Published":
      return "bg-green-50 text-green-700 border-green-200";
    case "Rejected":
    case "Conflict":
    case "Error":
    case "Publish Failed":
      return "bg-red-50 text-red-700 border-red-200";
    case "No Draft":
      return "bg-gray-50 text-gray-700 border-gray-200";
    default:
      return "bg-slate-50 text-slate-700 border-slate-200";
  }
};

// Moved from pageColumns.tsx (deleted in Task 6) — unchanged logic.
export const formatUnifiedDate = (
  timestamp: Date | { toDate?: () => Date } | null | undefined,
): string => {
  if (!timestamp) return "-";
  try {
    const date =
      timestamp instanceof Date
        ? timestamp
        : typeof (timestamp as { toDate?: () => Date }).toDate === "function"
          ? (timestamp as { toDate: () => Date }).toDate()
          : new Date(timestamp as unknown as string);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "-";
  }
};

export function auditToRow(job: HeuristicJob): UnifiedRow {
  const recommendation = job.results?.ai_recommendation?.valid_json;
  const priority = recommendation?.score?.priority_level ?? "";
  return {
    kind: "audit",
    id: job.id,
    title: recommendation?.title?.trim() || job.url,
    path: job.url,
    status: priority || "-",
    statusTone: priority ? getPriorityClass(priority) : "",
    score: recommendation?.score?.total ?? null,
    date: job.completed_at,
    raw: job,
  };
}

export function pageToRow(
  page: Page,
  wpVersions: Record<string, WordPressVersion[]>,
  isLoadingWpVersions: Record<string, boolean>,
): UnifiedRow {
  const workflowStatus = getPageWorkflowStatus(
    page,
    wpVersions[page.id],
    !!isLoadingWpVersions[page.id],
  );
  return {
    kind: "page",
    id: page.id,
    title: page.title || page.url,
    path: page.url,
    status: workflowStatus,
    statusTone: getWorkflowClass(workflowStatus),
    score: null,
    date: page.created_at,
    raw: page,
  };
}

export function ideaToRow(
  idea: Idea,
  ideaUiOverrides: Record<string, IdeaUiOverride | undefined>,
): UnifiedRow {
  const uiState = mapIdeaToUiState(idea, ideaUiOverrides[idea.id]);
  return {
    kind: "idea",
    id: idea.id,
    title: idea.title,
    path: idea.target_keyword || "-",
    status: uiState.label,
    statusTone: getIdeaStatusTone(uiState),
    score: null,
    date: idea.created_at,
    raw: idea,
  };
}
